import { TrainingAPI } from "../../api/TrainingAPI";
import {mapDataForHighchartsGroupedBar} from "../../mappers/model/GroupedBarChartMapper"
const trainingAPI = new TrainingAPI();
import { audioMessage as message } from "../../Components/Common/Notifications/audioMessage";
import { toPickerNotes } from "@mappers/notes.mapper.mjs";
import { readNoteApiError } from "@utils/noteApiError.mjs";

export const PLAYER_SET_LOADING = "[PLAYER]_SET_LOADING";
export const PLAYER_SET_COST_PER_MIN = "[PLAYER]_SET_COST_PER_MIN";
export const PLAYER_SET_FILTER = "[PLAYER]_SET_FILTER";
export const PLAYER_SET_TRAININGS = "[PLAYER]_SET_TRAININGS";
export const PLAYER_SET_PRACTICE_PLAN = "[PLAYER]_SET_PRACTICE_PLAN";
export const PLAYER_SET_TESTING_DATA = "[PLAYER]_SET_TESTING_DATA";
export const PLAYER_SET_TESTING_RADAR_DATA = "[PLAYER]_SET_TESTING_RADAR_DATA";
export const PLAYER_SET_TESTING_RADAR_METRICS = "[PLAYER]_SET_RADAR_METRICS";
export const PLAYER_SET_TESTING_STATS = "[PLAYER]_SET_TESTING_STATS";
export const PLAYER_SET_TESTING_STAT_DEFAULTS =
  "[PLAYER]_SET_TESTING_STAT_DEFAULTS";
export const PLAYER_SET_FP_DATA = "[PLAYER]_SET_FP_DATA";
export const PLAYER_SET_FPMS_DATA = "[PLAYER]_SET_FPMS_DATA";
export const PLAYER_SET_FP_METRICS = "[PLAYER]_SET_FP_METRICS";
export const PLAYER_SET_CHARTS = "[PLAYER]_PLAYER_SET_CHARTS";
export const PLAYER_SET_NOTES = "[PLAYER]_SET_NOTES";

export const setPlayerTestingStats = (data) => {
  return {
    type: PLAYER_SET_TESTING_STATS,
    data,
  };
};

export const setPlayerTestingStatDefaults = (data) => {
  return {
    type: PLAYER_SET_TESTING_STAT_DEFAULTS,
    data,
  };
};

export const setPlayerLoading = (data) => {
  return {
    type: PLAYER_SET_LOADING,
    data,
  };
};

export const setPlayerCostPerMin = (data) => {
  return {
    type: PLAYER_SET_COST_PER_MIN,
    data,
  };
};

export const setPlayerFilter = (data) => {
  return {
    type: PLAYER_SET_FILTER,
    data,
  };
};

export const setPlayerTrainings = (data) => {
  return {
    type: PLAYER_SET_TRAININGS,
    data,
  };
};

export const setPlayerPracticePlan = (data) => {
  return {
    type: PLAYER_SET_PRACTICE_PLAN,
    data,
  };
};

export const setPlayerTestingData = (data) => {
  return {
    type: PLAYER_SET_TESTING_DATA,
    data,
  };
};

export const setPlayerTestingRadarData = (data) => {
  return {
    type: PLAYER_SET_TESTING_RADAR_DATA,
    data,
  };
};

export const setPlayerTestingRadarMetrics = (data) => {
  return {
    type: PLAYER_SET_TESTING_RADAR_METRICS,
    data,
  };
};
export const setPlayerFPData = (data) => {
  return {
    type: PLAYER_SET_FP_DATA,
    data,
  };
};

export const setPlayerFPMSData = (data) => {
  return {
    type: PLAYER_SET_FPMS_DATA,
    data,
  };
};

export const setPlayerFPMetrics = (data) => {
  return {
    type: PLAYER_SET_FP_METRICS,
    data,
  };
};

export const getPlayerGPSData = (force_weekly = false) => {
  return async (dispatch, getState) => {
    await dispatch(setPlayerLoading({ gps: true }));
    try {
      const { players } = getState().team;
      const { filter } = getState().player;
      const player = players.find((p) => p.player_id === filter.player);
      if (!player?.player_id) {
        // This throw will be caught by the catch block below
        throw new Error(`Could not find player id: ${filter?.player}`);
      }
      
      const trainings = await trainingAPI.getPlayerCharts(
        player.player_id,
        filter.startDate,
        filter.endDate,
        filter.showWeekly,
        filter.session,
        filter.showPredictions
      );
      dispatch(setPlayerTrainings(trainings));

      const practicePlan = await trainingAPI.getPracticePlan(
        filter.startDate,
        filter.endDate,
        filter.player
      );
      dispatch(setPlayerPracticePlan(practicePlan));
    } catch (e) {
      console.error(e);
    }
    await dispatch(setPlayerLoading({ gps: false }));
  };
};

export const getPlayerDrillCostPerMin = () => {
  return async (dispatch, getState) => {
    const { player } = getState().player;
    try {
      const data = await trainingAPI.getDrillCostPerMin(player);
      dispatch(setPlayerCostPerMin(data));
    } catch (err) {
      console.error(err);
    }
  };
};

export const getPlayerTestingRadarData = () => {
  return async (dispatch, getState) => {
    // The radar mounts before a player is picked — filter.player starts null — so no id yet
    // is a normal state, not a failure. Leave the chart as it is rather than reporting one.
    // Guarded ahead of the loading flag, since returning inside the try would never clear it.
    if (!getState().player?.filter?.player) {
      return;
    }
    await dispatch(setPlayerLoading({ testingRadar: true }));
    try {
      const { players } = getState().team;
      const { filter, radarMetrics } = getState().player;
      const player = players.find((p) => p.player_id === filter.player);
      if (!player) {
        throw new Error(`Could not find player id: ${filter?.player}`);
      }
      if (!radarMetrics.every( (metric) => metric?.includes("-"))) {
        throw new Error(`Invalid metric found in radarMetrics list: ${radarMetrics?.join(", ")}`);
      }
      const data = await trainingAPI.getRadarData({
        start_date: filter.startDate,
				end_date: filter.endDate,
				metrics: radarMetrics,
        name: `${player?.first_name} ${player?.last_name}`
      });
      dispatch(setPlayerTestingRadarData(data));
    } catch (e) {
      console.error(e);
    }
    await dispatch(setPlayerLoading({ testingRadar: false }));
  };
};

export const getPlayerMovementSignatureData = () => {
  return async (dispatch, getState) => {
    // The page mounts before a player is picked — filter.player starts null — so no id yet
    // is a normal state, not a failure. Guarded ahead of the loading flag, since returning
    // inside the try would never clear it.
    if (!getState().player?.filter?.player) {
      return;
    }
    // /testingdata resolves the movement-signature metric ids from the team's chart mapping
    // and 400s when the team has none, so an unmapped team can never get data here. The
    // Force Plate tab already hides the chart in that case (TeamData guards the same way).
    // /teaminfo sends null for unmapped teams; undefined means it has not loaded yet, and
    // /players can win that race, so only a known-null mapping skips the fetch.
    if (getState().team?.info?.movement_sig_chart_mapping === null) {
      return;
    }
    await dispatch(setPlayerLoading({ fpmsData: true }));

    const { filter } = getState().player;
      
    const payload = {
      start_date: filter.startDate,
      end_date: filter.endDate,
      stats: filter.forceplateStats ? "Max" : "Avg",
      player_id: filter.player,
      metric_type: "forceplate",
      is_movement_sig: true
    }
    const isPayloadValid = () => {
      if (!payload.start_date || !payload.end_date || !payload.stats || !payload.player_id) {
        return false;
      }
      if (!Number.isInteger(payload.player_id) || payload.player_id <= 0) {
        return false;
      }
      return true;
    }
    
    try {
      if (!isPayloadValid()) {
        throw new Error('Invalid request parameters');
      }
      const data = await trainingAPI.getTestingData(payload);
      dispatch(setPlayerFPMSData(data));
    } catch (e) {
      console.error(e);
    }
    await dispatch(setPlayerLoading({ fpmsData: false }));
  };
};

export const changePlayerFPMetric = (key, value) => {
  return async (dispatch, getState) => {
    let metrics = [...getState().player.fpMetrics];
    metrics[key] = value;
    await dispatch(setPlayerFPMetrics(metrics));
  };
};

export const setPlayerNotes = (data) => ({ type: PLAYER_SET_NOTES, data });

// The date range of the configurator changes fast. Only the newest request may write to the store.
let latestPlayerNotesRequest = 0;

// `from` and `to` are `YYYY-MM-DD` dates. Without both, the picker has no range to list.
export const getTeamPlayerNotes = (from, to) => async (dispatch) => {
  const request = ++latestPlayerNotesRequest;
  if (!from || !to) {
    dispatch(setPlayerNotes([]));
    return;
  }
  try {
    const notes = await trainingAPI.getNotes(from, to);
    if (request === latestPlayerNotesRequest) dispatch(setPlayerNotes(toPickerNotes(notes)));
  } catch (e) {
    message.error(readNoteApiError(e).message);
  }
};
